// packages/web/src/components/common/BrandLogo.tsx
import { Box } from '@mui/material';
import * as React from 'react';

import { toAbsoluteUrl } from '@/lib/url-utils';

interface BrandLogoProps {
  src?: string | null;
  alt: string;
  size?: number;
  width?: number;
  height?: number;
  maxWidth?: number;
  preserveNaturalAspect?: boolean;
  fallbackSrc?: string;
  lazy?: boolean;
  priority?: 'high' | 'low' | 'auto';
  blurPlaceholder?: boolean;
  prefetch?: boolean;
  blurSrc?: string | null;
  blurFallback?: boolean; // Use CSS blur on main image if blurSrc fails
}

export function BrandLogo({
  src,
  alt,
  size = 40,
  width,
  height,
  maxWidth,
  preserveNaturalAspect = false,
  fallbackSrc = '/assets/brands/default/logo.webp',
  lazy = false,
  priority = 'auto',
  blurPlaceholder = false,
  prefetch = false,
  blurSrc,
  blurFallback = false,
}: BrandLogoProps) {
  // Convert relative URLs to absolute URLs for cross-domain iframe embedding support
  const absoluteSrc = toAbsoluteUrl(src);
  const absoluteFallbackSrc = toAbsoluteUrl(fallbackSrc);
  const absoluteBlurSrc = blurSrc ? toAbsoluteUrl(blurSrc) : null;

  const [imageSrc, setImageSrc] = React.useState(absoluteSrc || absoluteFallbackSrc);
  const [hasError, setHasError] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isInView, setIsInView] = React.useState(!lazy);
  const [blurImageFailed, setBlurImageFailed] = React.useState(false);
  const loadStartTime = React.useRef<number | null>(null);
  const imgRef = React.useRef<HTMLImageElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setImageSrc(absoluteSrc || absoluteFallbackSrc);
    setHasError(false);
    setIsLoaded(false);
    setBlurImageFailed(false);
    loadStartTime.current = performance.now();
  }, [absoluteSrc, absoluteFallbackSrc]);

  // Intersection Observer for lazy loading
  React.useEffect(() => {
    if (!lazy || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.1,
      },
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [lazy]);

  // Smart prefetching for likely next logos (Chunk 14)
  React.useEffect(() => {
    if (prefetch && imageSrc && typeof window !== 'undefined') {
      const img = document.createElement('img');
      img.src = imageSrc;
      // Preload in background, no need to handle load/error for prefetch
    }
  }, [prefetch, imageSrc]);

  // Viewport prediction prefetching - prefetch logos likely to come into view
  React.useEffect(() => {
    if (!lazy || !isInView || typeof window === 'undefined') return;

    // Prefetch logos that are likely to be viewed next based on viewport prediction
    const prefetchNearbyLogos = () => {
      // Get all brand logo elements in viewport
      const logoElements = document.querySelectorAll('[data-brand-logo]');
      const currentIndex = Array.from(logoElements).indexOf(containerRef.current);

      if (currentIndex === -1) return;

      // Prefetch next 3 logos
      for (let i = 1; i <= 3; i++) {
        const nextElement = logoElements[currentIndex + i] as HTMLElement;
        if (nextElement) {
          const nextSrc = nextElement.getAttribute('data-src');
          if (nextSrc && !nextElement.hasAttribute('data-prefetched')) {
            const img = document.createElement('img');
            img.src = nextSrc;
            nextElement.setAttribute('data-prefetched', 'true');
          }
        }
      }
    };

    // Small delay to allow DOM updates
    const timeoutId = setTimeout(prefetchNearbyLogos, 100);
    return () => clearTimeout(timeoutId);
  }, [lazy, isInView]);

  const handleError = () => {
    if (!hasError) {
      setImageSrc(fallbackSrc);
      setHasError(true);

      // Performance monitoring: track logo load errors (Chunk 15)
      if (loadStartTime.current) {
        const loadTime = performance.now() - loadStartTime.current;
        console.log(`[LOGO_PERF] Error loading ${imageSrc} after ${loadTime.toFixed(2)}ms`);
        // Could send to analytics service
      }
    }
  };

  const handleBlurError = () => {
    if (!blurImageFailed) {
      setBlurImageFailed(true);
      console.log('[BrandLogo] Blur image failed to load, using CSS blur fallback');
    }
  };

  const handleLoad = () => {
    setIsLoaded(true);

    // Performance monitoring: track logo load times (Chunk 15)
    if (loadStartTime.current) {
      const loadTime = performance.now() - loadStartTime.current;
      console.log(`[LOGO_PERF] Successfully loaded ${imageSrc} in ${loadTime.toFixed(2)}ms`);
      // Could send to analytics service
    }
  };

  // Determine if we should show blur placeholder
  const showBlurPlaceholder =
    blurPlaceholder && !isLoaded && (absoluteBlurSrc || (blurFallback && absoluteSrc));

  return (
    <Box
      ref={containerRef}
      data-brand-logo
      data-src={imageSrc}
      sx={{
        position: 'relative',
        width: preserveNaturalAspect ? 'auto' : (width || size),
        height: height || size,
        maxWidth: maxWidth || 'none',
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {/* Blur placeholder */}
      {showBlurPlaceholder && (
        <>
          {/* Actual blur image if available and not failed */}
          {absoluteBlurSrc && !blurImageFailed && (
            <Box
              component="img"
              src={absoluteBlurSrc}
              alt=""
              onError={handleBlurError}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: preserveNaturalAspect ? 'auto' : '100%',
                maxWidth: '100%',
                height: '100%',
                objectFit: 'contain',
                opacity: 0.8,
              }}
            />
          )}
          {/* CSS blur fallback if blur image failed or not available */}
          {((blurFallback && absoluteSrc && (blurImageFailed || !absoluteBlurSrc)) ||
            (blurImageFailed && blurFallback)) && (
            <Box
              component="img"
              src={absoluteSrc}
              alt=""
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: preserveNaturalAspect ? 'auto' : '100%',
                maxWidth: '100%',
                height: '100%',
                objectFit: 'contain',
                opacity: 0.8,
                filter: 'blur(10px)',
              }}
            />
          )}
        </>
      )}

      {/* Main image - only render when in view for lazy loading */}
      {isInView && (
        <Box
          ref={imgRef}
          component="img"
          src={imageSrc}
          alt={alt}
          loading={lazy ? 'lazy' : 'eager'}
          fetchPriority={priority === 'auto' ? undefined : priority}
          sx={{
            width: preserveNaturalAspect ? 'auto' : '100%',
            maxWidth: '100%',
            height: '100%',
            objectFit: 'contain',
            opacity: blurPlaceholder && !isLoaded ? 0 : 1,
            transition: blurPlaceholder ? 'opacity 0.3s ease-in-out' : undefined,
          }}
          onError={handleError}
          onLoad={handleLoad}
        />
      )}
    </Box>
  );
}
